
import React from "react";
import Layout from "@/components/layout/Layout";
import { SectionHeader } from "@/components/ui/section-header";
import { Card } from "@/components/ui/card";
import { HelpCircle, Users, BookOpen } from "lucide-react";
import { SEOHead } from "@/components/seo/SEOHead";
import { generateBreadcrumbSchema } from "@/lib/schema";
import { BreadcrumbNavigation } from "@/components/ui/breadcrumb-list";

const stats = [
  {
    number: "1000+",
    label: "Families Supported",
    icon: Users
  },
  {
    number: "250+",
    label: "Nursing Home Partners",
    icon: HelpCircle
  },
  {
    number: "98%",
    label: "Satisfaction Rate",
    icon: BookOpen
  }
];

const values = [
  {
    title: "Trust & Transparency",
    description: "We believe in complete honesty and clarity in everything we do, ensuring families can make informed decisions with confidence."
  },
  {
    title: "Empathy First",
    description: "Every interaction is guided by compassion and understanding, recognizing the emotional journey families are on."
  },
  {
    title: "Excellence in Support",
    description: "We're committed to providing exceptional guidance and support throughout the entire nursing home search process."
  }
];

export default function AboutPage() {
  // Create breadcrumbs
  const breadcrumbs = [
    { name: "Home", url: "/" },
    { name: "About", url: "/about" }
  ];

  // Generate breadcrumb schema
  const breadcrumbSchema = generateBreadcrumbSchema(breadcrumbs);

  return (
    <Layout>
      <SEOHead 
        title="About Grandpal | Supporting Families in Ireland"
        description="Learn how Grandpal is supporting families across Ireland to find the right nursing home care with compassion, clarity and expert guidance."
        canonicalUrl="/about"
        schema={breadcrumbSchema}
      />
      
      <div className="bg-gradient-to-b from-white to-grandpal-beige/20">
        <div className="container max-w-7xl py-16 md:py-24">
          <div className="container mb-4">
            <BreadcrumbNavigation items={breadcrumbs.map(b => ({ label: b.name, href: b.url }))} />
          </div>

          <SectionHeader 
            title="About Grandpal" 
            subtitle="Supporting families in finding the right nursing home care across Ireland" 
            centered 
          />
          
          {/* Mission Statement */}
          <div className="max-w-3xl mx-auto text-center mb-16">
            <p className="text-xl text-grandpal-gray leading-relaxed">
              At Grandpal, we understand that finding the right nursing home for a loved one is one of life's most important decisions. Our mission is to make this journey easier, more transparent, and more compassionate for families across Ireland.
            </p>
          </div>

          {/* Stats Section */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-20">
            {stats.map((stat, index) => <div key={index} className="text-center">
                <div className="flex justify-center mb-4">
                  <stat.icon className="w-12 h-12 text-grandpal-green" />
                </div>
                <div className="text-4xl font-bold text-grandpal-green-dark mb-2">{stat.number}</div>
                <div className="text-grandpal-gray">{stat.label}</div>
              </div>)}
          </div>

          {/* Values Section */}
          <div className="bg-white rounded-2xl shadow-sm border border-grandpal-beige p-8 md:p-12">
            <h2 className="text-3xl font-semibold text-center mb-12 text-grandpal-green-dark">Our Core Values</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {values.map((value, index) => <Card key={index} className="p-6 bg-gradient-to-b from-white to-grandpal-beige/10 border-none">
                  <h3 className="text-xl font-semibold mb-4 text-grandpal-green-dark">{value.title}</h3>
                  <p className="text-grandpal-gray leading-relaxed">{value.description}</p>
                </Card>)}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
